import {
  Component, ComponentFactory, ComponentFactoryResolver,
  Inject, OnInit, ViewChild, ViewContainerRef, Input, OnDestroy
} from '@angular/core';
import { ApiService } from '../../../service/api.service';
import { Device, SendingStatus } from '../../../model/device';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { timer, Subscription } from 'rxjs';
import { Filter } from '../../../model/page';
import { FilterComponent } from '../../filter-engine/filter.component';
import { FilterComponentFactory } from '../../filter-engine/filter-component-factory';
import { FilterEngineDirective } from '../../filter-engine/filter-engine.directive';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetailPopupComponent } from './device-detail.component';
import { NotificationService } from '../../notification/notification.service';
import { YesNoDialogComponent } from '../../dialogs/yesno-dialog/yesno-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss']
})
export class DevicesComponent implements OnInit, OnDestroy {

  loading = false;
  filterOpened = false;

  filterCriteriaButtonDefaultTitle = 'FILTER_ENGINE.FILTER_CRITERIA_DEFAULT_TITLE';
  filterCriteriaButtonTitle: string;

  displayedColumns: string[] =
    ['name', 'identifier', 'model', 'endPoint', 'description', 'lastCallDate', 'lastReadingRequestDate', 'lastConfigurationSendingDate',
      'sendingStatus', 'actions'];
  dataSource: MatTableDataSource<Device> = new MatTableDataSource<Device>();
  filters: Filter[] = [];
  filterCategories = [
    {
      key: 'device',
      label: 'FILTER_ENGINE.CATEGORY.DEVICE',
      order: 2,
      options: [
        {
          key: 'deviceName',
          label: 'FILTER_ENGINE.CRITERIA_LABEL.DEVICE_NAME',
          fullLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_NAME',
          element: 'input',
          order: 1,
          options: {
            criteriaKey: 'deviceName',
            criteriaLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_NAME'
          }
        },
        {
          key: 'deviceIdentifier',
          label: 'FILTER_ENGINE.CRITERIA_LABEL.DEVICE_IDENTIFIER',
          fullLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_IDENTIFIER',
          element: 'input',
          order: 1,
          options: {
            criteriaKey: 'deviceIdentifier',
            criteriaLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_IDENTIFIER'
          }
        },
        {
          key: 'deviceDescription',
          label: 'FILTER_ENGINE.CRITERIA_LABEL.DEVICE_DESCRIPTION',
          fullLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_DESCRIPTION',
          element: 'input',
          order: 2,
          options: {
            criteriaKey: 'deviceDescription',
            criteriaLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_DESCRIPTION'
          }
        },
        {
          key: 'deviceTypeModel',
          label: 'FILTER_ENGINE.CRITERIA_LABEL.DEVICE_TYPE_MODEL',
          fullLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_TYPE_MODEL',
          element: 'input',
          order: 3,
          options: {
            criteriaKey: 'deviceTypeModel',
            criteriaLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_TYPE_MODEL'
          }
        },
        {
          key: 'deviceCommunicationEndpoint',
          label: 'FILTER_ENGINE.CRITERIA_LABEL.DEVICE_COMMUNICATION_ENDPOINT',
          fullLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_COMMUNICATION_ENDPOINT',
          element: 'input',
          order: 4,
          options: {
            criteriaKey: 'deviceCommunicationEndpoint',
            criteriaLabel: 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_COMMUNICATION_ENDPOINT'
          }
        }
      ]
    }
  ];

  SendingStatus: any = SendingStatus;

  refreshSubscription: Subscription = null;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  @ViewChild(FilterEngineDirective, { static: true }) filterEngine: FilterEngineDirective;

  @Input() deviceName: string = null;
  @Input() deviceIdentifier: string = null;

  constructor(
    @Inject('environment') private environment,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private filterComponentFactory: FilterComponentFactory,
    private componentFactoryResolver: ComponentFactoryResolver,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.dataSource.sort = this.sort;
    this.filterCategories.sort((a, b) => a.order - b.order);
    this.filterCategories.forEach(criteria => criteria.options.sort((a, b) => a.order - b.order));
    this.resetFilterCriteriaButtonTitle();

    if (this.deviceName != null) {
      const deviceNameFilter = new Filter();
      deviceNameFilter.criteriaKey = 'deviceName';
      deviceNameFilter.criteriaLabel = 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_NAME';
      deviceNameFilter.value = this.deviceName;
      deviceNameFilter.label = this.deviceName;

      this.filters.push(deviceNameFilter);
    } else if (this.deviceIdentifier != null) {
      const deviceIdentifierFilter = new Filter();
      deviceIdentifierFilter.criteriaKey = 'deviceIdentifier';
      deviceIdentifierFilter.criteriaLabel = 'FILTER_ENGINE.CRITERIA_FULL_LABEL.DEVICE_IDENTIFIER';
      deviceIdentifierFilter.value = this.deviceIdentifier;
      deviceIdentifierFilter.label = this.deviceIdentifier;

      this.filters.push(deviceIdentifierFilter);
    } else {
      // Try to restore the filters and sort from the cache
      const filterSetting = localStorage.getItem('brio-list-filter');
      if (filterSetting) {
        this.filters = JSON.parse(filterSetting);
        if (this.filters.length > 0) {
          this.filterOpened = true;
        }
      }
      const sortSetting = localStorage.getItem('brio-list-sort');
      if (sortSetting) {
        const sortConfig = JSON.parse(sortSetting);
        this.sort.sort(sortConfig);
      }
    }

    setTimeout(() => {
      this.getDevices();
    });

    this.refreshSubscription = timer(0, this.environment.refreshInterval)
      .subscribe(() => {
        if (!this.loading) {
          this.getDevicesStatus();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription != null) {
      this.refreshSubscription.unsubscribe();
    }
  }

  configure(id: string) {
    const selectedDevice = this.dataSource.data.find(device => device.id === id);
    if (selectedDevice.sendingStatus === SendingStatus.Undefined || selectedDevice.sendingStatus === SendingStatus.Unknown) {
      return;
    }
    const dialogRef = this.dialog.open(DeviceDetailPopupComponent, {
      width: '900px',
      disableClose: false,
      data: { device: selectedDevice }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.refresh) {
        // By default, set the device as refreshing
        selectedDevice.loading = true;

        setTimeout(() => {
          this.getDevicesStatus();
        }, 2000);
      }
    });
  }

  getDevices(): void {
    this.loading = true;
    this.notificationService.showLoader();
    this.apiService.devices(this.paginator.pageSize, this.paginator.pageIndex, this.filters)
      .subscribe(devicePage => {
        this.paginator.pageSize = devicePage.page.limit;
        this.paginator.pageIndex = devicePage.page.currentPage;
        this.paginator.length = devicePage.page.total;
        this.dataSource.data = devicePage.content;
        this.loading = false;
        this.notificationService.hideLoader();
        this.getDevicesStatus();
      }, () => {
        this.loading = false;
        this.notificationService.hideLoader();
      });
  }

  getDevicesStatus(): void {
    this.loading = true;
    this.apiService.comstatus(this.dataSource.data.map((d) => d.id))
      .subscribe(statuses => {
        this.dataSource.data.forEach(device => {
          const comStatus = statuses.find(status => status.id === device.id);
          const newStatus = comStatus?.status || SendingStatus.Unknown;

          if (comStatus.lastCallDate) {
            device.lastCallDate = comStatus.lastCallDate;
          }
          if (comStatus.lastReadingRequestDate) {
            device.lastReadingRequestDate = comStatus.lastReadingRequestDate;
          }
          if (comStatus.lastConfigurationSendingDate) {
            device.lastConfigurationSendingDate = comStatus.lastConfigurationSendingDate;
          }
          if (newStatus !== device.sendingStatus) {
            device.sendingStatus = newStatus;
          }
          device.loading = false;
        });
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  requestConfiguration(id: string) {
    const selectedDevice = this.dataSource.data.find(device => device.id === id);

    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: null,
      height: null,
      data: {
        Content: this.translateService.instant('DEVICES.CONFIRM_CONFIGURATION_REQUEST')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // By default, set the device as refreshing
        selectedDevice.loading = true;

        this.loading = true;
        this.apiService.requestConfiguration(id)
          .subscribe(_ => {
            setTimeout(() => {
              this.getDevicesStatus();
            }, 2000);
          }, () => {
            this.loading = false;
            selectedDevice.loading = false;
          });
      }
    });
  }

  showFilter() {
    this.filterOpened = !this.filterOpened;
  }

  clearFilter() {
    this.filters = [];
    this.paginator.pageIndex = 0;
    this.storeFilter();
    this.getDevices();
    this.resetFilterCriteriaButtonTitle();
  }

  resetFilterCriteriaButtonTitle(): void {
    this.filterCriteriaButtonTitle = this.filterCriteriaButtonDefaultTitle;
  }

  addField(criteria: any) {
    this.filterCriteriaButtonTitle = criteria.fullLabel;

    let componentFactory: ComponentFactory<FilterComponent>;
    componentFactory = this.filterComponentFactory.createComponent(criteria.element, this.componentFactoryResolver);

    const componentRef = this.initViewContainerRef().createComponent(componentFactory);

    componentRef.instance.data = criteria.options;

    componentRef.instance.dispatchFilterValue.subscribe((componentOutput: Filter) => {
      if (componentOutput) {
        this.checkFilterDuplicate(componentOutput);
      }
    });
  }

  checkFilterDuplicate(filter: Filter): void {
    if (!this.filters.find(f => f.criteriaKey === filter.criteriaKey && f.value === filter.value)) {
      this.filters.push(filter);
      this.paginator.pageIndex = 0;
      this.storeFilter();
      this.getDevices();
    }
  }

  sortChange(evt: Sort): void {
    const sortConfig = {
      id: evt.active,
      start: evt.direction
    };
    localStorage.setItem('brio-list-sort', JSON.stringify(sortConfig));
  }

  initViewContainerRef(): ViewContainerRef {
    let viewContainerRef;

    viewContainerRef = this.filterEngine.viewContainerRef;
    viewContainerRef.clear();

    return viewContainerRef;
  }

  onDeletePreFilter(filter: Filter): void {
    const index: number = this.filters.findIndex((currentFilter: Filter) => currentFilter === filter);
    this.filters.splice(index, 1);
    this.paginator.pageIndex = 0;
    this.storeFilter();
    this.getDevices();
  }

  private storeFilter(): void {
    localStorage.setItem('brio-list-filter', JSON.stringify(this.filters));
  }
}
